import { useState } from "react";
import { Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { SlideOver } from "@/components/ui/slide-over";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";
import { objectiveMissingLabel } from "@/types/sim-sandbox.types";
import { useSimulatableOrders } from "@/features/production-orders/hooks";
import { ObjectivePicker } from "./ObjectivePicker";
import { objectiveMeta, objectivesFor } from "./objective-meta";

interface Props {
  value: SimulationObjectiveChoice | null;
  onChange: (next: SimulationObjectiveChoice) => void;
  /** Linha base do cenário; null ⇒ linha nova (sem ordens, logo sem objetivo de prazo). */
  lineId: string | null;
}

function summary(c: SimulationObjectiveChoice): string {
  const meta = objectiveMeta(c.kind);
  if (c.kind === "OP_ON_TIME") {
    return c.productionOrderId ? "Entregar a OP no prazo" : (meta?.question ?? "Objetivo");
  }
  if (c.kind === "TIME_TO_QUANTITY") {
    return Number.isFinite(c.quantity)
      ? `Tempo para ${c.quantity.toLocaleString("pt-BR")} un`
      : (meta?.question ?? "Objetivo");
  }
  const h = Number.isFinite(c.window.hours) ? `${c.window.hours}h` : "—";
  return c.kind === "OEE_IMPROVEMENT" ? `Melhorar o OEE · ${h}` : `Quanto produzo em ${h}`;
}

// O objetivo fica SEMPRE visível no cenário e pode ser trocado ali.
// Trocar NÃO dispara execução: com o custo de cada rodada (configuração atual + variações no motor
// pesado, serializadas por organização), explorar as perguntas não pode enfileirar lotes sem que o
// usuário peça. A execução acontece só no clique em Simular.
export function ObjectiveChip({ value, onChange, lineId }: Props) {
  const [open, setOpen] = useState(false);
  const missing = objectiveMissingLabel(value);
  // Sem isto o objetivo de prazo era oferecido aqui SEM as ordens: o campo respondia sempre "esta
  // linha não tem Ordens de Produção" e o Simular ficava bloqueado sem saída.
  const ordersQuery = useSimulatableOrders(lineId);

  return (
    <>
      <Button
        variant={missing ? "outline" : "secondary"}
        size="sm"
        onClick={() => setOpen(true)}
        title="O que você quer saber nesta simulação"
      >
        <Target className="h-4 w-4" />
        {value ? summary(value) : "Escolher objetivo"}
      </Button>

      <SlideOver open={open} onOpenChange={setOpen} title="O que você quer saber?" width={420}>
        <div className="space-y-3 p-4">
          <ObjectivePicker
            value={value}
            onChange={onChange}
            orders={ordersQuery.data ?? []}
            options={objectivesFor({ hasBaseLine: !!lineId })}
          />
          {ordersQuery.isError ? (
            <Notify.QueryError
              error={ordersQuery.error}
              onRetry={() => void ordersQuery.refetch()}
            />
          ) : null}
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Trocar a pergunta não roda nada — o resultado na tela continua sendo a resposta do
            objetivo anterior até você simular de novo.
          </p>
          <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
            Fechar
          </Button>
        </div>
      </SlideOver>
    </>
  );
}
